// src/styles/base/tokens.ts
/**
 * The single source of colour for the dashboard.
 *
 * Components, the MUI palette (foundations.ts), the :root CSS variables (applyCssVariables),
 * Recharts props and the Excel export all read from `colors`. Nothing else should carry a hex
 * literal: name the role here and use it there. See docs/BRAND_RESTYLE.md §2–§3.
 */

import { isProduction } from '@/utils/environment';

/** Infinitum brand palette (Brand Playbook v3.0, pp. 21–24). Reference values, not roles. */
export const brand = {
    darkPurple: '#110326',
    blue: '#1C1F74',
    pink: '#F77385',
    charcoal: '#24201F',
    white: '#FFFFFF',
    teal: '#0097A7',
    amber: '#E8951F',
    violet: '#7C6DAF',
    coolGrey: '#7B8FA1',
} as const;

/**
 * The colour laid over the page's ground at `strength` (0–1), as the playbook draws its 75/50/25 %
 * tints. The ground is white, so in the light theme this is the playbook's tint exactly; in the
 * dark preview a pale tint becomes a faint lift off the dark ground, which is the same job.
 */
export const tint = (hex: string, strength: number): string => mix(ground, hex, strength);

/** `rgba()` of a hex colour, for translucent fills and borders. */
export const alpha = (hex: string, opacity: number): string => {
    const h = hex.replace('#', '');
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/** `hex` mixed `amount` (0–1) of the way toward `toward`: tint() mixes toward white, this toward anything */
export const mix = (hex: string, toward: string, amount: number): string => {
    const ch = (c: string, i: number) => parseInt(c.replace('#', '').substring(i, i + 2), 16);
    const out = [0, 2, 4].map((i) =>
        Math.round(ch(hex, i) * (1 - amount) + ch(toward, i) * amount)
            .toString(16)
            .padStart(2, '0')
    );
    return `#${out.join('')}`.toUpperCase();
};

/**
 * House colours: our additions to the playbook, each made because a brand colour could not do
 * the job. Named so they are chosen, not stumbled on; Simon and BB may still replace the green
 * and red, which the brand does not define.
 */
export const house = {
    /** Success: MUI's green 800, 5.1:1 under white text */
    green: '#2E7D32',
    /** Error: MUI's red 800, 5.6:1 on white and 5.0:1 on panelFill (its 700 fell short at 4.4:1) */
    red: '#C62828',
    /** Amber 45 % toward Charcoal, so warnings can be *text*: 5.4:1 on white, 4.8:1 on panelFill */
    darkAmber: mix(brand.amber, brand.charcoal, 0.45),
    /** Teal 25 % toward Charcoal, so white small text on Teal passes: 5.1:1 (plain Teal 3.5:1) */
    darkTeal: mix(brand.teal, brand.charcoal, 0.25),
    /** Dark preview's ground: Dark Purple and Charcoal halved, so the dark reads as the brand's, not #000 */
    night: mix(brand.darkPurple, brand.charcoal, 0.5),
} as const;

/**
 * Colour variants previewed from the style sheet page (`/style-sheet`, behind the build badge).
 * Non-production only: the choice sits in this browser's localStorage and applies on reload, so
 * a variant can be tried on alpha or beta without a branch. Production always gets the default.
 */
export const COLOUR_PREVIEWS = [
    { id: 'default', label: 'Charcoal band (current)' },
    { id: 'light-band', label: 'No dark band' },
] as const;
export type ColourPreview = (typeof COLOUR_PREVIEWS)[number]['id'];
export const COLOUR_PREVIEW_KEY = 'infinitum.colourPreview';

const readColourPreview = (): ColourPreview => {
    if (isProduction) return 'default';
    try {
        const v = globalThis.localStorage?.getItem(COLOUR_PREVIEW_KEY);
        return COLOUR_PREVIEWS.some((p) => p.id === v) ? (v as ColourPreview) : 'default';
    } catch {
        return 'default';
    }
};

export const colourPreview = readColourPreview();

/**
 * The light/dark choice, from the toggle in the app bar (ThemeToggle). Light unless the user picks
 * dark (Style Guide §9), so the OS setting is deliberately not followed. Every role below is fixed
 * at module load, so a change is stored here and applied by a reload, as the previews are.
 *
 * Dark began as a style-sheet preview under COLOUR_PREVIEW_KEY; a browser still holding that is
 * read as a dark choice, so nobody trying it on beta is switched back.
 */
export type ThemeChoice = 'light' | 'dark';
export const THEME_KEY = 'infinitum.theme';
/**
 * Dark reaches production only when this is true. Until then the toggle shows on alpha and beta
 * only: the dark theme still has open items in TODO.md, and `main` fast-forwards from `next`.
 */
const DARK_THEME_IN_PRODUCTION = false;
export const darkThemeAvailable = !isProduction || DARK_THEME_IN_PRODUCTION;

const readThemeChoice = (): ThemeChoice => {
    if (!darkThemeAvailable) return 'light';
    try {
        const ls = globalThis.localStorage;
        return ls?.getItem(THEME_KEY) === 'dark' || ls?.getItem(COLOUR_PREVIEW_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
        return 'light';
    }
};

export const themeChoice = readThemeChoice();

/** Store the choice and reload, so every role is worked out again. Light is the absence of a choice. */
export const applyThemeChoice = (choice: ThemeChoice): void => {
    try {
        if (choice === 'dark') localStorage.setItem(THEME_KEY, 'dark');
        else localStorage.removeItem(THEME_KEY);
        // Retire the old preview value with the first explicit choice, or "light" could never win
        if (localStorage.getItem(COLOUR_PREVIEW_KEY) === 'dark') localStorage.removeItem(COLOUR_PREVIEW_KEY);
    } catch {
        // Storage blocked: nothing to apply, and light stands
        return;
    }
    window.location.reload();
};

/**
 * Typeface previews, on the same terms as the colour variants: non-production, this browser only,
 * on reload. Houschka Alt Pro itself is unlicensed for the web (AESTHETICS_ROSTER §6), so these are
 * open-licence stand-ins; `figures` is the dashboard's test (BRAND_RESTYLE.md: do columns of numbers
 * align?), measured from the font files 2026-10-22. Only the chosen face is downloaded, by `loadPreviewFont` in index.tsx.
 */
export const FONT_PREVIEWS = [
    { id: 'default', label: 'Nunito Sans (current)', family: 'Nunito Sans Variable', figures: 'tabular by default' },
    { id: 'outfit', label: 'Outfit', family: 'Outfit Variable', figures: 'tabular via tnum' },
    { id: 'jost', label: 'Jost', family: 'Jost Variable', figures: 'tabular via tnum' },
    { id: 'urbanist', label: 'Urbanist', family: 'Urbanist Variable', figures: 'proportional only' },
    { id: 'quicksand', label: 'Quicksand', family: 'Quicksand Variable', figures: 'proportional only' },
    { id: 'nunito', label: 'Nunito', family: 'Nunito Variable', figures: 'tabular by default' },
    { id: 'lexend', label: 'Lexend', family: 'Lexend Variable', figures: 'proportional only' },
] as const;
export type FontPreview = (typeof FONT_PREVIEWS)[number]['id'];
export const FONT_PREVIEW_KEY = 'infinitum.fontPreview';

const readFontPreview = (): FontPreview => {
    if (isProduction) return 'default';
    try {
        const v = globalThis.localStorage?.getItem(FONT_PREVIEW_KEY);
        return FONT_PREVIEWS.some((p) => p.id === v) ? (v as FontPreview) : 'default';
    } catch {
        return 'default';
    }
};

export const fontPreview = readFontPreview();
const fontStack = `"${FONT_PREVIEWS.find((p) => p.id === fontPreview)!.family}", Arial, sans-serif`;
const dark = themeChoice === 'dark';
/** The light-band preview is a light-theme question; dark has its own band */
const lightBand = colourPreview === 'light-band' && !dark;
/*
 * The dark theme (Material 3's dark scheme, mapped onto ours): ink and rules take light tones of
 * Blue, since Blue itself vanishes on a dark ground; surfaces separate by lightness, not shadow.
 */
const bandInk = lightBand ? brand.charcoal : brand.white;
/** Blue lifted for a dark ground: the "tone 80" of M3, as ink, rules and the first series */
const blueOnDark = mix(brand.blue, brand.white, 0.62);

/** The page's ground, which tint() mixes toward */
const ground: string = dark ? house.night : brand.white;

/** Colour by role. Change a value here and every screen, chart and export follows. */
export const colors = {
    /**
     * Top navigation bar. White, with the full-colour logo: the playbook's main logo, "use this
     * as much as possible". Dark Purple read as too heavy across the whole width (LM, 2026-10-20).
     * Everything drawn on the bar takes its colour from `appBarText`, so flipping the bar dark
     * again is these three lines.
     */
    appBar: dark ? mix(house.night, brand.white, 0.06) : brand.white,
    appBarText: dark ? blueOnDark : brand.blue,
    appBarLogo: dark ? '/images/infinitum-logo-reverse.png' : '/images/infinitum-logo.png',
    /** The page, and raised surfaces on it (cards, menus, drawers) */
    ground,
    paper: dark ? mix(house.night, brand.white, 0.04) : brand.white,
    /** Structural colour: card and table headers, primary buttons, links */
    primary: dark ? blueOnDark : brand.blue,
    primaryDark: dark ? mix(brand.blue, brand.white, 0.45) : brand.darkPurple,
    primaryLight: dark ? tint(blueOnDark, 0.3) : tint(brand.blue, 0.75),
    /** Text and icons on a primary fill */
    onPrimary: dark ? house.night : brand.white,
    /**
     * Title bars of drawers, dialogs and the date picker: Blue in both themes. A full-width bar in
     * the dark theme's light Blue would glare, and white on Blue reads on any ground.
     */
    titleBar: brand.blue,
    titleBarText: brand.white,
    /** Where the date picker's title gradient ends */
    titleBarFade: dark ? mix(brand.blue, house.night, 0.6) : tint(brand.blue, 0.75),
    /** Row rules in tables: MUI's own light grey on white, a faint light line on dark */
    hairline: dark ? alpha(brand.white, 0.12) : 'rgba(224, 224, 224, 1)',
    /**
     * The strip under the app bar and the controls on it: the one solid dark band on the page.
     * Everything drawn on the band takes these roles (see band.ts), so the light-band preview
     * is these lines.
     */
    band: lightBand ? brand.white : dark ? mix(house.night, '#000000', 0.4) : brand.charcoal,
    bandText: bandInk,
    /** Outlines of the band's controls, and its dividers */
    bandLine: alpha(bandInk, 0.3),
    bandHover: alpha(bandInk, 0.1),
    bandDisabled: alpha(bandInk, 0.45),
    /** The chosen half of a toggle on the band */
    bandSelected: lightBand ? brand.blue : brand.white,
    bandSelectedText: lightBand ? brand.white : brand.charcoal,
    /** A light band needs an edge; a dark one is its own */
    bandRule: lightBand ? tint(brand.coolGrey, 0.35) : 'transparent',
    /**
     * Tiles and chart/table headers: a pale fill with a Blue rule, so Blue is the accent rather
     * than the wallpaper. Solid Blue everywhere was "a little on the nose" (SG, 2026-10-21).
     * Applied through panels.ts; flipping them dark again is these three lines.
     */
    panelFill: tint(brand.coolGrey, dark ? 0.16 : 0.12),
    panelText: dark ? mix(brand.white, brand.coolGrey, 0.1) : brand.charcoal,
    panelRule: dark ? blueOnDark : brand.blue,
    /** The accent: highlights, the one thing a chart is about. Never small text on white (2.7:1). */
    accent: brand.pink,
    accentContrastText: brand.darkPurple,
    /** The main data colour in charts (bars, map bubbles): Teal, "data visualisation" in the playbook */
    chartData: brand.teal,

    text: dark ? mix(brand.white, brand.coolGrey, 0.1) : brand.charcoal,
    /** 75 % Charcoal, 7.1:1. Not Cool Grey, the playbook's "metadata" colour, which fails at 3.3:1 */
    textSecondary: dark ? mix(brand.white, house.night, 0.3) : tint(brand.charcoal, 0.75),
    border: tint(brand.coolGrey, 0.5),
    gridLine: tint(brand.coolGrey, 0.25),
    surfaceSubtle: tint(brand.coolGrey, 0.08),

    /** The brand defines no green or red: these are house colours */
    success: dark ? '#66BB6A' : house.green,
    error: dark ? '#EF5350' : house.red,
    /** Brand-designated for warnings; never as text on white (2.4:1) */
    warning: brand.amber,
    /** For warning *text*: Amber itself fails on white (2.4:1) */
    warningText: dark ? brand.amber : house.darkAmber,
    warningContrastText: brand.darkPurple,
    info: brand.teal,

    /** Overview KPI tiles: each a role of its own, not a borrowed status colour */
    kpiImpressions: brand.blue,
    /** Dark Purple is 1.09:1 against the dark ground, so the tile vanished: Violet 25 % toward Charcoal, 6.2:1 under white */
    kpiSpend: dark ? mix(brand.violet, brand.charcoal, 0.25) : brand.darkPurple,
    kpiBookings: house.darkTeal,
    /** A fill under white text, so never `error`, which the dark preview lightens for use as text (3.5:1) */
    kpiIssues: house.red,
    /** 75 % Charcoal, 7.1:1 under white text; Cool Grey gave 3.3:1 */
    kpiNoIssues: tint(brand.charcoal, 0.75),

    /**
     * Categorical chart series, in order (BRAND_RESTYLE §3.4); Cool Grey is never a series, only
     * `seriesOther`. Brand hues in shades chosen by measurement (2026-10-22; two of the three
     * directors are red-green colour blind): every colour at least 3:1 on white (WCAG 1.4.11, a
     * line or bar must be seen), and the closest pair as far apart as a search of brand shades
     * allowed under normal, deuteranopic and protanopic vision (Machado 2009, as Chrome emulates):
     * worst ΔE 23. The brand's own five scored 13 with three below 3:1; Okabe–Ito, the accepted
     * colour-blind palette, scores 25 but only with colours a thin line on white cannot carry
     * (yellow 1.3:1). Hue alone still cannot keep five lines apart, so line charts also take
     * `seriesDash` (chartConfig). `chartData`, the single-series colour, stays the brand's Teal.
     */
    series: dark
        ? [blueOnDark, brand.pink, brand.teal, brand.amber, brand.violet]
        : [
              brand.blue,
              mix(brand.pink, brand.charcoal, 0.35),
              brand.teal,
              mix(brand.amber, brand.charcoal, 0.15),
              mix(brand.violet, brand.charcoal, 0.55),
          ],
    /** Ranks six to ten where a chart keys colour on rank (Parameters) */
    seriesExtra: dark
        ? [
              mix(brand.blue, brand.white, 0.8),
              mix(brand.teal, brand.white, 0.5),
              mix(brand.amber, brand.white, 0.5),
              mix(brand.violet, brand.white, 0.5),
              mix(brand.pink, brand.white, 0.5),
              brand.coolGrey,
          ]
        : [
              tint(brand.blue, 0.5),
              tint(brand.teal, 0.5),
              tint(brand.amber, 0.5),
              tint(brand.violet, 0.5),
              tint(brand.pink, 0.5),
              brand.darkPurple,
          ],
    seriesOther: brand.coolGrey,
    /** Recharts takes fonts as props, not from the theme */
    chartFontFamily: fontStack,
    /**
     * Single-hue sequential scale (heatmaps): tints of this over the ground. On the dark preview
     * Teal, not `blueOnDark`: that Blue is lifted toward white and so nearly grey, and faint cells
     * read as grey on purple (LM, 2026-10-29). Lightness carries the order; Teal keeps its chroma.
     */
    sequentialBase: dark ? brand.teal : brand.blue,
    /**
     * The map basemap on the dark theme, recoloured from Carto's `dark_nolabels` (RegionSpikeMap).
     * Unused in light, where Carto's `light_all` stands as drawn.
     *
     * The palette of Snazzy Maps' "Assassin's Creed IV" (style 72543, LM's pick, 2026-11-04): sage
     * land well clear of a near-black sea (2.22:1; the night-purple version managed 1.48), roads
     * and borders a lighter sage. Land this light no longer leaves room for the spike colours
     * (Teal 1.9:1 on it), so every spike carries a `mapCasing` outline (2.9:1 on the land) and the
     * edge, not the fill, separates mark from ground: the cartographer's casing. Earlier tries are
     * in TODO-NOTDOING.md (the light map's sea) and git (`832dce7`: neutral night sea).
     */
    mapLand: '#4D6059',
    mapSea: '#24282B',
    mapLine: '#7F8D89',
    /** Place names: Carto's grey (#9B, 2.4:1 on the sage) recoloured to this, 5.7:1 */
    mapInk: mix(house.night, brand.white, 0.92),
    /** Outline round each spike on the dark map */
    mapCasing: mix(house.night, '#000000', 0.35),
    /** The hollow "too few to judge" spike: Cool Grey is 2.0:1 on the sage, so lifted on dark */
    mapHollow: dark ? mix(brand.white, house.night, 0.3) : brand.coolGrey,
    /** Excel export header fill (ARGB without alpha) */
    excelHeader: brand.blue.replace('#', ''),
    /**
     * MUI's grey scale for our own neutral fills and rules, reversed on the dark preview so grey[50]
     * is still the faintest lift. Ours, not the theme's: MUI draws tooltips and chips from
     * palette.grey, and reversing that made dark tooltips pale under white text. On dark, the steps
     * are lifts of the ground rather than MUI's neutral greys, which read as grey patches on purple.
     */
    grey: dark
        ? {
              50: mix(house.night, brand.white, 0.03),
              100: mix(house.night, brand.white, 0.17),
              200: mix(house.night, brand.white, 0.32),
              300: mix(house.night, brand.white, 0.42),
              500: mix(house.night, brand.white, 0.6),
          }
        : { 50: '#fafafa', 100: '#f5f5f5', 200: '#eeeeee', 300: '#e0e0e0', 500: '#9e9e9e' },
    /** Whether the dark preview is showing: for the few things roles cannot carry (MUI mode, map tiles) */
    isDark: dark,

    /** Nunito Sans (self-hosted, @fontsource-variable), with the brand's sanctioned Arial fallback */
    fontStack,
};

/** Mirror the role colours into :root, so index.scss's var(--…) follows the same source. */
export const applyCssVariables = (root: HTMLElement = document.documentElement): void => {
    const vars: Record<string, string> = {
        '--primary-color': colors.primary,
        '--font-stack': colors.fontStack,
    };
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
};
